import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import SortArrowIcon from '../icons/SortArrowIcon';
import NamedBox from '../atoms/NamedBox';
import { useTableSort, SortDirection } from '../hooks/useTableSort';
import { SpecialTeamsCollegeProductionRow } from '../types/CollegeProductionData';
import { COLOR_BACKGROUND_WARM, COLOR_BACKGROUND_LIGHT, COLOR_MIDDLE_GRAY, COLOR_TEXT, COLOR_WHITE, COLOR_BLUE } from '../styles/colors';

interface PuntKickoffStatRow {
  year: number;
  teamName: string;
  punt: number;
  puntYard: number;
  puntAverage: number;
  kickoff: number;
  kickoffTouchback: number;
  touchbackPct: string;
  kickoffOutOfBounds: number;
}

type ColumnAlign = 'center' | 'left';

type DerivedColumnKey =
  | 'puntAverage'
  | 'touchbackPct'
  | 'fgPct'
  | 'expPlusMinus'
  | 'xpPct'
  | 'fg10to19'
  | 'fg10to19Pct'
  | 'fg20to29'
  | 'fg20to29Pct'
  | 'fg30to39'
  | 'fg30to39Pct'
  | 'fg40to49'
  | 'fg40to49Pct'
  | 'fg50to59'
  | 'fg50to59Pct'
  | 'fg60Plus'
  | 'fg60PlusPct';

type ColumnKey = keyof SpecialTeamsCollegeProductionRow | DerivedColumnKey;

interface ColumnConfig {
  key: ColumnKey;
  label: string;
  align: ColumnAlign;
}

const COLUMNS: ColumnConfig[] = [
  { key: 'year', label: 'Year', align: 'center' },
  { key: 'teamName', label: 'Team', align: 'left' },
  { key: 'punt', label: 'Punts', align: 'center' },
  { key: 'puntYard', label: 'Punt Yards', align: 'center' },
  { key: 'puntAverage', label: 'Punt Average', align: 'center' },
  { key: 'kickoff', label: 'Kickoffs', align: 'center' },
  { key: 'kickoffTouchback', label: 'Touchbacks', align: 'center' },
  { key: 'touchbackPct', label: 'Touchback %', align: 'center' },
  { key: 'kickoffOutOfBounds', label: 'KO OOB', align: 'center' },
  { key: 'fieldGoalMade', label: 'FGM', align: 'center' },
  { key: 'fieldGoalAttempts', label: 'FGA', align: 'center' },
  { key: 'fgPct', label: '%', align: 'center' },
  { key: 'expectedFieldGoalPercent', label: 'Exp. FG %*', align: 'center' },
  { key: 'expPlusMinus', label: 'Exp. +/-', align: 'center' },
  { key: 'fieldGoalLong', label: 'Long FG', align: 'center' },
  { key: 'xpMade', label: 'XP Made', align: 'center' },
  { key: 'xpAttempts', label: 'XP Att.', align: 'center' },
  { key: 'xpPct', label: '%', align: 'center' },
  { key: 'fg10to19', label: '10-19', align: 'center' },
  { key: 'fg10to19Pct', label: '10-19%', align: 'center' },
  { key: 'fg20to29', label: '20-29', align: 'center' },
  { key: 'fg20to29Pct', label: '20-29%', align: 'center' },
  { key: 'fg30to39', label: '30-39', align: 'center' },
  { key: 'fg30to39Pct', label: '30-39%', align: 'center' },
  { key: 'fg40to49', label: '40-49', align: 'center' },
  { key: 'fg40to49Pct', label: '40-49%', align: 'center' },
  { key: 'fg50to59', label: '50-59', align: 'center' },
  { key: 'fg50to59Pct', label: '50-59%', align: 'center' },
  { key: 'fg60Plus', label: '60+', align: 'center' },
  { key: 'fg60PlusPct', label: '60+%', align: 'center' },
];

/**
 * Registry of derived columns. Each entry knows how to compute its raw numeric
 * value from a row (used for sorting) and how to format it for display.
 * Add a new computed column here without touching render/sort logic.
 */
const COMPUTED_COLUMNS: Record<string, {
  compute: (row: SpecialTeamsCollegeProductionRow) => number;
  format: (value: number) => string;
}> = {
  puntAverage: {
    compute: row => (row.punt ? row.puntYard / row.punt : 0),
    format: value => value.toFixed(1),
  },
  touchbackPct: {
    compute: row => (row.kickoff ? (row.kickoffTouchback / row.kickoff) * 100 : 0),
    format: value => `${value.toFixed(1)}%`,
  },
};

const RANGE_COLUMNS: Record<string, { madeKey: keyof SpecialTeamsCollegeProductionRow; attKey: keyof SpecialTeamsCollegeProductionRow }> = {
  fg10to19: { madeKey: 'fg10to19Made', attKey: 'fg10to19Attempts' },
  fg20to29: { madeKey: 'fg20to29Made', attKey: 'fg20to29Attempts' },
  fg30to39: { madeKey: 'fg30to39Made', attKey: 'fg30to39Attempts' },
  fg40to49: { madeKey: 'fg40to49Made', attKey: 'fg40to49Attempts' },
  fg50to59: { madeKey: 'fg50to59Made', attKey: 'fg50to59Attempts' },
  fg60Plus: { madeKey: 'fg60PlusMade', attKey: 'fg60PlusAttempts' },
};

const RANGE_PCT_COLUMNS: Record<string, { madeKey: keyof SpecialTeamsCollegeProductionRow; attKey: keyof SpecialTeamsCollegeProductionRow }> = {
  fg10to19Pct: { madeKey: 'fg10to19Made', attKey: 'fg10to19Attempts' },
  fg20to29Pct: { madeKey: 'fg20to29Made', attKey: 'fg20to29Attempts' },
  fg30to39Pct: { madeKey: 'fg30to39Made', attKey: 'fg30to39Attempts' },
  fg40to49Pct: { madeKey: 'fg40to49Made', attKey: 'fg40to49Attempts' },
  fg50to59Pct: { madeKey: 'fg50to59Made', attKey: 'fg50to59Attempts' },
  fg60PlusPct: { madeKey: 'fg60PlusMade', attKey: 'fg60PlusAttempts' },
};

function toPercent(made: number, attempts: number): number {
  if (!attempts) return 0;
  return (made / attempts) * 100;
}

function formatPercent(value: number, withSign = false): string {
  const prefix = withSign && value > 0 ? '+' : '';
  return `${prefix}${value.toFixed(1)}%`;
}

function getDerivedValue(row: SpecialTeamsCollegeProductionRow, key: ColumnKey): number | null {
  if (key === 'fgPct') {
    return toPercent(Number(row.fieldGoalMade), Number(row.fieldGoalAttempts));
  }
  if (key === 'expPlusMinus') {
    return toPercent(Number(row.fieldGoalMade), Number(row.fieldGoalAttempts)) - (Number(row.expectedFieldGoalPercent) || 0);
  }
  if (key === 'xpPct') {
    return toPercent(Number(row.xpMade), Number(row.xpAttempts));
  }
  if (key === 'expectedFieldGoalPercent') {
    return Number(row.expectedFieldGoalPercent) || 0;
  }

  const rangePct = RANGE_PCT_COLUMNS[key];
  if (rangePct) {
    return toPercent(Number(row[rangePct.madeKey]), Number(row[rangePct.attKey]));
  }

  return null;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  headerName: {
    fontWeight: 700,
  },
  tableWrapper: {
    width: '100%',
    overflowX: 'auto' as const,
    backgroundColor: '#ffffff',
  },
  table: {
    width: '100%',
    minWidth: 960,
    borderCollapse: 'collapse' as const,
    border: `1px solid ${COLOR_BACKGROUND_WARM}`,
    fontSize: theme.typography.pxToRem(16),
  },
  theadRow: {
    height: 40,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e0e0e0',
  },
  th: {
    overflow: 'hidden',
    border: `1px solid ${COLOR_BACKGROUND_WARM}`,
    borderTop: `2px solid ${COLOR_MIDDLE_GRAY}`,
    padding: theme.spacing(1),
    whiteSpace: 'nowrap' as const,
    color: COLOR_TEXT,
    fontWeight: 700,
    textAlign: 'left' as const,
    userSelect: 'none' as const,
    cursor: 'pointer',

    '&:first-child': {
      width: 80,
      minWidth: 80,
    },

    '&:last-child': {
      borderRight: 'none',
    },
  },
  sortLabel: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.25),
  },
  sortIcon: {
    opacity: 1,
    color: COLOR_MIDDLE_GRAY,

    '& svg': {
      display: 'block',
      width: 9,
      height: 'auto',
    },
  },
  alignCenter: {
    textAlign: 'center' as const,
  },
  alignLeft: {
    textAlign: 'left' as const,
  },
  bodyRow: {
    height: 44,
    backgroundColor: COLOR_WHITE,

    '&:nth-child(odd)': {
      backgroundColor: COLOR_BACKGROUND_LIGHT,
    },
  },
  td: {
    fontWeight: 600,
    color: '#1e1e1e',
    padding: theme.spacing(0.75, 1),
    whiteSpace: 'nowrap' as const,
    borderRight: '1px solid #f0f0f0',

    '&:last-child': {
      borderRight: 'none',
    },
  },
}), { name: 'SpecialTeamYearlyPuntKickoffStatsTable' });

interface SpecialTeamYearlyPuntKickoffStatsTableProps {
  className?: string;
  data?: SpecialTeamsCollegeProductionRow[] | null;
}

export default function SpecialTeamYearlyPuntKickoffStatsTable(props: SpecialTeamYearlyPuntKickoffStatsTableProps) {
  const { className, data = [] } = props;
  const classes = useStyles();

  const getSortValue = (row: SpecialTeamsCollegeProductionRow, key: ColumnKey): number | string => {
    const computed = COMPUTED_COLUMNS[key as string];
    if (computed) {
      return computed.compute(row);
    }

    const range = RANGE_COLUMNS[key];
    if (range) {
      return Number(row[range.madeKey]) || 0;
    }

    const derived = getDerivedValue(row, key);
    if (derived != null) {
      return derived;
    }

    return row[key as keyof SpecialTeamsCollegeProductionRow] as number | string;
  };

  const { sortKey, sortDirection, handleSort, sortedRows: sortedData } = useTableSort<SpecialTeamsCollegeProductionRow, ColumnKey>({
    rows: data,
    initialSortKey: 'year',
    initialDirection: 'desc',
    nextKeyDirection: 'desc',
    sortRows: (rows, key, direction) => {
      rows.sort((first, second) => {
        const firstValue = getSortValue(first, key);
        const secondValue = getSortValue(second, key);

        let comparison = 0;
        if (typeof firstValue === 'number' && typeof secondValue === 'number') {
          comparison = firstValue - secondValue;
        } else {
          comparison = String(firstValue).localeCompare(String(secondValue));
        }

        return direction === 'asc' ? comparison : -comparison;
      });

      return rows;
    },
  });

  return (
    <NamedBox
      className={clsx(classes.root, className)}
      headerNameClassName={classes.headerName}
      name="Yearly Punt & Kickoff Stats & Yearly Field Goal Stats"
    >
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead>
            <tr className={classes.theadRow}>
              {COLUMNS.map(column => {
                const isActive = column.key === sortKey;

                return (
                  <th
                    key={column.key}
                    className={clsx(classes.th)}
                  >
                    <TableSortLabel
                      active={isActive}
                      direction={isActive ? sortDirection : 'asc'}
                      className={classes.sortLabel}
                      hideSortIcon={false}
                      IconComponent={(iconProps) => (
                        <SortArrowIcon
                          {...iconProps}
                          className={`${iconProps.className} ${classes.sortIcon}`}
                          style={{
                            color: isActive ? COLOR_BLUE : '#b5b5b5',
                          }}
                        />
                      )}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedData.map(row => (
              <tr key={row.year} className={classes.bodyRow}>
                {COLUMNS.map(column => {
                  const alignClass = column.align === 'left' ? classes.alignLeft : classes.alignCenter;
                  const computed = COMPUTED_COLUMNS[column.key as string];
                  const range = RANGE_COLUMNS[column.key];
                  const derived = getDerivedValue(row, column.key);
                  let cellValue: string | number;

                  if (computed) {
                    cellValue = computed.format(computed.compute(row));
                  } else if (range) {
                    cellValue = `${Number(row[range.madeKey]) || 0} for ${Number(row[range.attKey]) || 0}`;
                  } else if (column.key === 'expPlusMinus') {
                    cellValue = formatPercent(derived || 0, true);
                  } else if (derived != null) {
                    cellValue = formatPercent(derived);
                  } else {
                    cellValue = row[column.key as keyof SpecialTeamsCollegeProductionRow] ?? 0;
                  }

                  return (
                    <td key={column.key} className={clsx(classes.td, alignClass)}>
                      {cellValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </NamedBox>
  );
}
